import { hasTileData, type TileData } from "./types";

export type TileStatus = "loading" | "loaded" | "empty" | "error";
export type TileEntry = {
  status: TileStatus;
  data?: TileData;
  error?: Error;
  refreshing?: boolean;
  revision?: number;
  revalidated?: boolean;
};
export type TileStore = ReadonlyMap<string, TileEntry>;

export type TileAction =
  | { type: "request"; ids: string[]; refresh?: boolean }
  | { type: "cancel"; ids: string[] }
  | { type: "fulfilled"; id: string; data: TileData; refresh?: boolean }
  | { type: "failed"; id: string; error: Error }
  | { type: "reset" };

export const emptyStore: TileStore = new Map();

export function tileReducer(store: TileStore, action: TileAction): TileStore {
  switch (action.type) {
    case "reset":
      return new Map();
    case "request": {
      const next = new Map(store);
      for (const id of action.ids) {
        const cur = next.get(id);
        if (action.refresh && cur?.data) {
          next.set(id, { ...cur, refreshing: true, error: undefined, revalidated: false });
        } else if (!cur || cur.status === "error") {
          next.set(id, { status: "loading", refreshing: !!action.refresh });
        }
      }
      return next;
    }
    case "cancel": {
      const next = new Map(store);
      for (const id of action.ids) {
        const cur = next.get(id);
        if (cur) next.set(id, { ...cur, refreshing: false, status: cur.data ? cur.status : "error" });
      }
      return next;
    }
    case "fulfilled": {
      const next = new Map(store);
      next.set(action.id, {
        status: hasTileData(action.data) ? "loaded" : "empty",
        data: action.data,
        revision: (store.get(action.id)?.revision ?? 0) + 1,
        revalidated: action.refresh || store.get(action.id)?.revalidated,
      });
      return next;
    }
    case "failed": {
      const next = new Map(store);
      const cur = store.get(action.id);
      next.set(
        action.id,
        cur?.data ? { ...cur, refreshing: false, error: action.error } : { status: "error", error: action.error }
      );
      return next;
    }
    default:
      return store;
  }
}
